/// <amd-module name="Build/Pipelines/Tabs/Branches/BranchesDataService" />
import { BuildResult, BuildStatus } from "Build/Client/Contracts/Build";
import { BranchesViewItem as DataProviderItem, HistogramEntry } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import * as Resources from "Build/Pipelines/Resources";
import { HistogramStates, IHistogramItem } from "Build/Shared/Components/Histogram";
import { ObservableValue } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { ago, duration } from "VSSUI/Utilities/Date";

// the server returns the raw values; we'll calculate the histogram fields (value, tooltip, href) on the client
export type RichHistogramEntry = HistogramEntry & IHistogramItem;

export interface BranchesViewItem extends Omit<DataProviderItem, "branchHistory"> {
    builds: RichHistogramEntry[];
    href: string;
    latestBuild: HistogramEntry | undefined;
}

export interface IBranchesDataService extends IVssService {
    data: ObservableValue<BranchesViewItem[]>;
    fetchData: (definitionId: number, repositoryId: number, ignoreCache: boolean) => void;
}

const maxBuildsPerBranch = 15;

class BranchesDataService extends VssService implements IBranchesDataService {
    private _data: ObservableValue<BranchesViewItem[]>;

    public get data(): ObservableValue<BranchesViewItem[]> {
        return this._data;
    }

    public fetchData(definitionId: number, repositoryId: number, ignoreCache: boolean) {
        // on page load use default request parameters so that data provider is not called twice. Otherwise add definitionId as parameter
        const requestParameters = ignoreCache ? { definitionId, repositoryId } : {};

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService
            .getDataAsync<DataProviderItem[]>("ms.vss-build-web.branches-data-provider", requestParameters, ignoreCache)
            .then(branches => {
                if (branches) {
                    const linkingService = this.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
                    this._data.value = processData(branches, definitionId, linkingService);
                }
            });
    }

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._initialize();
    }

    public _serviceRestart(pageContext: IVssPageContext): void {
        super._serviceRestart(pageContext);
        this._initialize();
    }

    private _initialize() {
        this._data = new ObservableValue<BranchesViewItem[]>([]);
    }
}

export function processData(data: DataProviderItem[], definitionId: number, linkingService: IBuildLinkingService): BranchesViewItem[] {
    const viewBranches: BranchesViewItem[] = [];
    for (const branch of data) {
        const builds = branch.branchHistory.map(item => ({
            ...item,
            value: calculateHistogramValue(item.queueTime, item.startTime, item.finishTime),
            state: calculateHistogramState(item.status, item.result),
            href: linkingService.getBuildUrl(item.id),
            tooltip: calculateTooltip(item.status, item.result, item.buildNumber, item.queueTime, item.startTime, item.finishTime)
        }));

        builds.sort((a, b) => {
            const aTime = a.startTime ? a.startTime.getTime() : a.queueTime.getTime();
            const bTime = b.startTime ? b.startTime.getTime() : b.queueTime.getTime();
            return aTime - bTime;
        });

        viewBranches.push({
            ...branch,
            latestBuild: branch.branchHistory.find(b => b.id === branch.latestBuildId),
            href: linkingService.getPipelinesUrl(definitionId, branch.repositoryId, branch.branchId),
            builds: builds.slice(-maxBuildsPerBranch)
        });
    }

    viewBranches.sort((a, b) => (a.isDefaultBranch ? -1 : b.isDefaultBranch ? 1 : localeIgnoreCaseComparer(a.sourceBranch, b.sourceBranch)));

    return viewBranches;
}

function calculateHistogramValue(queueTime: Date, startTime: Date | undefined, finishTime: Date | undefined): number {
    if (finishTime) {
        return finishTime.getTime() - (startTime !== undefined ? startTime.getTime() : queueTime.getTime());
    } else if (startTime) {
        return Date.now() - startTime.getTime();
    } else {
        // return zero for queued builds. they haven't actually run yet
        return 0;
    }
}

function calculateHistogramState(status: number, result: number | undefined): string {
    switch (status) {
        case BuildStatus.Cancelling:
        case BuildStatus.InProgress:
            return HistogramStates.Running;
        case BuildStatus.Completed:
            switch (result) {
                case BuildResult.Canceled:
                case BuildResult.Failed:
                    return HistogramStates.Failure;
                case BuildResult.PartiallySucceeded:
                    return HistogramStates.Warning;
                case BuildResult.Succeeded:
                    return HistogramStates.Success;
                default:
                    return HistogramStates.Unknown;
            }
        default:
            return HistogramStates.Unknown;
    }
}

function calculateHistogramResult(result: number): string {
    switch (result) {
        case BuildResult.Canceled:
            return Resources.HistogramResultCanceled;
        case BuildResult.Failed:
            return Resources.HistogramResultFailed;
        case BuildResult.PartiallySucceeded:
            return Resources.HistogramResultPartiallySucceeded;
        case BuildResult.Succeeded:
            return Resources.HistogramResultSucceeded;
        default:
            return "";
    }
}

function calculateTooltip(
    status: number,
    result: number | undefined,
    buildNumber: string,
    queueTime: Date,
    startTime: Date | undefined,
    finishTime: Date | undefined
): string {
    switch (status) {
        case BuildStatus.Completed:
            const resultString = calculateHistogramResult(result || BuildResult.None);
            const completedTime = ago(finishTime!).toLocaleLowerCase();
            return format(Resources.HistogramTooltipCompleted, buildNumber, resultString, completedTime, duration(startTime!, finishTime!));
        case BuildStatus.InProgress:
        case BuildStatus.Cancelling:
            return format(Resources.HistogramTooltipInProgress, buildNumber, duration(startTime!, new Date()));
        default:
            return format(Resources.HistogramTooltipNotStarted, buildNumber, ago(queueTime));
    }
}

Services.add("IBranchesDataService", { serviceFactory: BranchesDataService });
